import React from 'react';
import { F, linkedIds } from '../utils.jsx';
import { TABLE_IDS } from '../globals.js';

const DIAGNOSTIC_STAGES = [
  { key: 'Market clarity',         label: 'Claridad de mercado',        color: '#6B5CA5', desc: 'Todavía no tienen claro quién necesita su oferta ni por qué.' },
  { key: 'Commercial focus',       label: 'Foco comercial',      color: '#2D5F99', desc: 'Saben qué venden, pero no a quién priorizar.' },
  { key: 'Opportunity generation', label: 'Generación de oportunidades',color: '#9A6212', desc: 'No generan oportunidades de forma constante.' },
  { key: 'Process & conversion',   label: 'Proceso y conversión',  color: '#A0521A', desc: 'Hay actividad, pero las oportunidades no avanzan hacia una decisión.' },
  { key: 'Measurement & control',  label: 'Medición y control', color: '#1E7B4F', desc: 'Les faltan datos para aprender de su esfuerzo comercial.' },
];

const OIKE_SERVICES = {
  'Oike Diagnostic':    { color: '#6B5CA5', icon: '🔬' },
  'Oike System Design': { color: '#2D5F99', icon: '🏗️' },
  'Oike Enablement':    { color: '#9A6212', icon: '🎓' },
  'Oike Advisory':      { color: '#1E7B4F', icon: '📡' },
};

export default function AccountStrategyTab({
  account, accountIntake,
  allSolutions, currentSolIds,
  showSolPicker, setShowSolPicker,
  availableSolutions, addSolutionToAccount, removeSolutionFromAccount, removingSol,
  newSolName, setNewSolName, creatingSol, createNewSolution,
  strategyData, setStrategyData,
  savingStrategy, setSavingStrategy,
  approachRec, approachLoading, generateApproach,
  setAccDetailTab,
  api, onUpdateRecord,
}) {
  const intakeFields = accountIntake?.fields || null;
  const intakeSubmitted = intakeFields?.['Status'] === 'Submitted';

  const diagStage = approachRec?.diagnosticStage
    ? DIAGNOSTIC_STAGES.find(s => s.key === approachRec.diagnosticStage)
    : null;
  const svcCfg = approachRec?.suggestedService ? OIKE_SERVICES[approachRec.suggestedService] : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Intake Diagnosis ── */}
      <div className="card">
        <div className="card-header" style={{ justifyContent: 'space-between' }}>
          <h3>Diagnóstico de la cotización</h3>
          {!accountIntake && (
            <button className="action-btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setAccDetailTab('intake')}>
              Go to Intake →
            </button>
          )}
        </div>

        {!accountIntake && (
          <p style={{ fontSize: 12, color: 'var(--globant-muted)', fontStyle: 'italic' }}>
            No intake yet for this account. Generate a link from the Intake tab so the client can fill in the diagnosis.
          </p>
        )}

        {accountIntake && !intakeSubmitted && (
          <div style={{ padding: '12px 14px', background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>⏳</span>
            <div style={{ fontSize: 12, color: '#9A6212' }}>El cliente todavía no completó el formulario. Esperando su respuesta.</div>
          </div>
        )}

        {accountIntake && intakeSubmitted && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              ['Main Challenge',        intakeFields['Main Challenge']],
              ['Goals',                 intakeFields['Goals']],
              ['Current Sales Motion',  intakeFields['Current Sales Motion']],
              ['Sales Team Size',       intakeFields['Sales Team Size']],
              ['Pipeline (last 90d)',   intakeFields['Pipeline Last 90 Days']],
              ['Prospecting Activity',  intakeFields['Prospecting Activity']],
            ].filter(([, v]) => v).map(([label, value]) => (
              <div key={label} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 10, alignItems: 'baseline' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
                <span style={{ fontSize: 13, color: 'var(--globant-text)', lineHeight: 1.6 }}>{value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Suggested Approach ── */}
      {accountIntake && intakeSubmitted && (
        <div className="card">
          <div className="card-header" style={{ justifyContent: 'space-between' }}>
            <h3>💡 Suggested Approach</h3>
            <button className="action-btn btn-primary"
              style={{ fontSize: 11, background: 'rgba(167,139,250,0.15)', color: '#6B5CA5', border: '1px solid rgba(167,139,250,0.3)' }}
              onClick={generateApproach} disabled={approachLoading}>
              {approachLoading ? 'Generando…' : approachRec ? 'Volver a generar' : 'Generar enfoque'}
            </button>
          </div>

          {!approachRec && !approachLoading && (
            <p style={{ fontSize: 12, color: 'var(--globant-muted)', fontStyle: 'italic' }}>
              Generate the approach to see the diagnostic stage, recommended service, and how to talk to this client.
            </p>
          )}

          {approachLoading && (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--globant-muted)', fontSize: 12 }}>
              ⏳ Analyzing intake…
            </div>
          )}

          {approachRec && !approachLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {diagStage && (
                  <div style={{ flex: 1, minWidth: 200, padding: '10px 14px', borderRadius: 10, background: `${diagStage.color}10`, border: `1px solid ${diagStage.color}30` }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: diagStage.color, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Etapa del diagnóstico</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--globant-text)', marginBottom: 4 }}>{diagStage.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--globant-muted)' }}>{diagStage.desc}</div>
                  </div>
                )}
                {svcCfg && approachRec.suggestedService && (
                  <div style={{ padding: '10px 14px', borderRadius: 10, background: `${svcCfg.color}10`, border: `1px solid ${svcCfg.color}30`, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: svcCfg.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>Servicio recomendado</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--globant-text)' }}>{svcCfg.icon} {approachRec.suggestedService}</div>
                  </div>
                )}
              </div>

              {approachRec.rootCause && (
                <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(30,42,44,0.03)', border: '1px solid var(--globant-border)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Causa de fondo</div>
                  <div style={{ fontSize: 13, color: 'var(--globant-text)', lineHeight: 1.6 }}>{approachRec.rootCause}</div>
                </div>
              )}

              {approachRec.angle && (
                <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(91,191,181,0.05)', border: '1px solid rgba(91,191,181,0.2)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--globant-green)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Cómo hablarles</div>
                  <div style={{ fontSize: 13, color: 'var(--globant-text)', lineHeight: 1.6 }}>{approachRec.angle}</div>
                </div>
              )}

              {approachRec.openingQuestion && (
                <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(251,191,36,0.05)', border: '1px solid rgba(251,191,36,0.2)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#9A6212', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Pregunta de apertura</div>
                  <div style={{ fontSize: 13, color: 'var(--globant-text)', lineHeight: 1.6, fontStyle: 'italic' }}>"{approachRec.openingQuestion}"</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Strategic Notes ── */}
      <div className="card">
        <div className="card-header"><h3>Notas estratégicas</h3></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 2, minWidth: 180 }}>
              <label style={{ fontSize: 11, color: 'var(--globant-muted)', display: 'block', marginBottom: 4 }}>Objetivo con la empresa</label>
              <input value={strategyData.objective || ''} onChange={e => setStrategyData(p => ({ ...p, objective: e.target.value }))}
                placeholder="¿Qué quieres lograr con esta empresa?"
                style={{ width: '100%', background: 'var(--globant-input)', border: '1px solid var(--globant-border)', borderRadius: 7, padding: '7px 10px', color: 'var(--globant-text)', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 11, color: 'var(--globant-muted)', display: 'block', marginBottom: 4 }}>Fecha objetivo</label>
              <input type="date" value={strategyData.targetDate || ''} onChange={e => setStrategyData(p => ({ ...p, targetDate: e.target.value }))}
                style={{ width: '100%', background: 'var(--globant-input)', border: '1px solid var(--globant-border)', borderRadius: 7, padding: '7px 10px', color: 'var(--globant-text)', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--globant-muted)', display: 'block', marginBottom: 4 }}>Notas y enfoque</label>
            <textarea value={strategyData.angle || ''} onChange={e => setStrategyData(p => ({ ...p, angle: e.target.value }))}
              rows={3} placeholder="Contexto, forma de entrar y diferenciales para esta empresa…"
              style={{ width: '100%', background: 'var(--globant-input)', border: '1px solid var(--globant-border)', borderRadius: 7, padding: '7px 10px', color: 'var(--globant-text)', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
        </div>
      </div>

      {/* ── Linked Services ── */}
      <div className="card">
        <div className="card-header" style={{ justifyContent: 'space-between' }}>
          <h3>Servicios vinculados</h3>
          <button className="action-btn btn-ghost" style={{ fontSize: 10 }} onClick={() => setShowSolPicker(!showSolPicker)}>
            {showSolPicker ? 'Cerrar' : 'Agregar'}
          </button>
        </div>

        {currentSolIds.length > 0 ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: showSolPicker ? 12 : 0 }}>
            {currentSolIds.map(sid => {
              const sol = allSolutions.find(s => s.id === sid);
              const solName = sol ? F(sol, 'Name') : sid;
              return (
                <span key={sid} className="badge badge-accent" style={{ fontSize: 12, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {solName}
                  <span style={{ cursor: 'pointer', opacity: 0.6, fontSize: 10 }} onClick={() => removeSolutionFromAccount(sid)} title="Quitar">
                    {removingSol === sid ? '⏳' : '✕'}
                  </span>
                </span>
              );
            })}
          </div>
        ) : <p style={{ color: 'var(--globant-muted)', fontSize: 12, marginBottom: showSolPicker ? 12 : 0 }}>Todavía no hay servicios vinculados</p>}

        {showSolPicker && (
          <div style={{ padding: '12px', background: 'var(--globant-darker)', borderRadius: 8, marginTop: 8 }}>
            {availableSolutions.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--globant-muted)', fontWeight: 600, marginBottom: 6 }}>ELEGIR UNO EXISTENTE</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {availableSolutions.map(s => (
                    <button key={s.id} className="action-btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => addSolutionToAccount(s.id)}>+ {F(s, 'Name')}</button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div style={{ fontSize: 11, color: 'var(--globant-muted)', fontWeight: 600, marginBottom: 6 }}>O CREAR UNO NUEVO</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="input-field" style={{ flex: 1, fontSize: 12, padding: '6px 10px' }} placeholder="Nombre del servicio…" value={newSolName} onChange={e => setNewSolName(e.target.value)} onKeyDown={e => e.key === 'Enter' && createNewSolution()} />
                <button className="action-btn btn-primary" style={{ fontSize: 11 }} onClick={createNewSolution} disabled={!newSolName.trim() || creatingSol}>{creatingSol ? 'Creando…' : 'Crear y vincular'}</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <button className="action-btn btn-primary" disabled={savingStrategy}
        style={{ alignSelf: 'flex-end', fontSize: 13 }}
        onClick={async () => {
          if (!account || !api) return;
          setSavingStrategy(true);
          try {
            const fields = { Strategy: JSON.stringify(strategyData) };
            if (onUpdateRecord) onUpdateRecord('accounts', account.id, fields);
            await api.updateRecord(TABLE_IDS.accounts, account.id, fields);
          } catch(e) { console.error('Strategy save error', e); }
          finally { setSavingStrategy(false); }
        }}>
        {savingStrategy ? 'Guardando…' : 'Guardar notas'}
      </button>
    </div>
  );
}
